
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { cloneDeep, isEqual } from 'lodash-es';

import LIST_MANAGERS from '@/apollo/managers/ListManagers.gql';
import UPDATE_MANAGER from '@/apollo/managers/UpdateManager.gql';
import GET_PAYMENT_CARDS from '@/apollo/payment/GetPaymentCards.gql';
import AddSubscription from './Modal/add-subcription-modal.vue';
import ExceedDialog from './Modal/exceed-dialog.vue';
import AddAddtionalManager from './Modal/add-addtional-manager.vue';
import DefaultContentLayout from '@/components/scaffold/DefaultContentLayoutNew.vue';
import ManagerNewSearchFilters from '@/views/manage/ManagerNewSearchFilters.vue';
import CardPagination from '@/components/card/PaginationNew.vue';
import EmptyListPlaceholder from '@/components/common/EmptyListPlaceholder.vue';
import ManagerListCard from '@/components/managers/ManagerListCard.vue';
import UiButton from '@/components/button/ui-button.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import ManagerInviteNew from '@/components/managers/ManagerInviteNew.vue';
import PlusIcon from '@/components/icons/PlusIconNew.vue';
import HoveredIcon from '@/components/icons/HoveredIcon.vue';
import ActionPanel from '@/components/common/ActionsPanel.vue';

import Breadcrumbs from '@/components/breadcrumbs/BreadcrumbsNew.vue';
import { makeNameQuery } from '@/helpers/queryHelperFunctions';
import {
	getFiltersDataFromQuery,
	joinQueryValues,
} from '@/helpers/router/prepareFiltersQuery';
import {
	getPaginationDataFromQuery,
	getPaginationQuery,
} from '@/helpers/router/preparePaginationQuery';

import managersCollectionFiltersTemplate from '@/data/filtersTemplates/managersCollectionFiltersTemplate';

import PaginationConfig from '@/entities/PaginationConfig';
import ManagersCollectionFiltersQuery from '@/entities/filters/ManagersCollectionFiltersQuery';
import RouteNames from '@/entities/RouteNames';
import paginationConfig from '@/data/paginationConfig';

import getActiveManagersCountCondition from '@/helpers/queryConditions/getActiveManagersCountCondition';
import Message from '@/data/notificationsMessages';
import { NOTIFICATION } from '@/entities/Events';
import {
	Company,
	Manager,
	User,
	Manager_Bool_Exp,
	Mutation_RootUpdate_Manager_By_PkArgs,
	Payment_Card,
} from '@/generated/graphql';

import { FetchResult } from 'apollo-link';
import { getEmptyFiltersResultMessage } from '@/helpers/lists/emptyListMessages';
import CardBodyItem from '@/components/card/CardBodyItem.vue';
import NavGrayArrow from '@/components/icons//NavGrayArrow.vue';
import EmptyListImageManagers from '@/components/icons/emptyListImages/EmptyListImageManagers.vue';
import getStateQueryConditions from '@/helpers/locations/stateQueryConditions';

interface UserInterface {
	id: string | null;
	email: string | null;
	givenName: string | null;
	familyName: string | null;
	valid: boolean;
	company: string | null;
	processing: boolean;
}

@Component({
	components: {
		EmptyListImageManagers,
		ExceedDialog,
		CardBodyItem,
		NavGrayArrow,
		AddAddtionalManager,
		DefaultContentLayout,
		ManagerNewSearchFilters,
		CardPagination,
		AddSubscription,
		EmptyListPlaceholder,
		LoadingOverlay,
		ManagerListCard,
		UiButton,
		HoveredIcon,
		ManagerInviteNew,
		PlusIcon,
		ActionPanel,
		Breadcrumbs,
	},
	apollo: {
		managers: {
			query: LIST_MANAGERS,
			variables() {
				return this.managerArgs;
			},
			update(data) {
				this.paginationConfig.total =
					data.manager_aggregate.aggregate.count;
				this.adminCount = data.manager_aggregate.aggregate.count;
				this.paginationConfig.loadedCount = data.manager.length;

				// PAYMENT CHECK COMMENTED: Original subscription-based manager count checks
				/*
				if (this.isManagersCountUnlimited) {
					this.activeManagersCount = 0;
					this.accessAllowedManagersCount = 0;
					return data.manager;
				}
				*/

				// Modified to set high number for unlimited managers
				this.activeManagersCount = data.activeManagersCount?.aggregate?.count || 0;
				this.accessAllowedManagersCount = 999999; // Set to high number instead of subscription-based count
				// Original: data.subscriptions[0].subscription_plan.subscription_plan_access_permission.managers_count;

				return data.manager;
			},
		},
		/* PAYMENT CHECK COMMENTED: Payment card query kept but not used
		paymentCardsFullInfo: {
			query: GET_PAYMENT_CARDS,
			variables() {
				return {
					userId: this.currentUser.id,
					subscriptionId: this.currentSubscriptionId,
				};
			},
			update(data) {
				this.paymentCards = data.payment_card;
			},
		},
		*/
	},
})
export default class ManagersCollectionPageNew extends Vue {
	@Prop({ type: Object, required: true }) readonly company!: Company;
	@Prop({ type: Object, required: true }) readonly currentUser!: User;

	inviteDialog: boolean = false;
	showSubscriptionDialog: boolean = false;
	searched: boolean = false;
	showAddAddtionalManagerDialog: boolean = false;
	tableView: boolean = true;
	activeManagersCount: number = 0;
	accessAllowedManagersCount: number = 999999; // Set to high number to allow unlimited managers
	adminCount: number = 0;
	maangerCount: number = 0;
	managers: Manager[] | undefined = undefined;
	showExceeded: boolean = false;
	managersFilterd: Manager[] | undefined = undefined;
	paymentCards: Payment_Card[] = [];
	managerStatusProcessing: boolean = false;
	users: UserInterface[] = [
		{
			id: null,
			email: '',
			givenName: '',
			familyName: '',
			company: null,
			valid: false,
			processing: false,
		},
	];
	userTemplate: UserInterface = {
		id: null,
		email: null,
		givenName: null,
		familyName: null,
		company: null,
		valid: false,
		processing: false,
	};
	$confirm: any;

	paginationConfig: PaginationConfig = { ...paginationConfig };

	managerFilters: ManagersCollectionFiltersQuery = cloneDeep(
		managersCollectionFiltersTemplate
	);

	containerClasses: string = 'h-full px-4 sm:px-6 md:px-8';

	/* PAYMENT CHECK COMMENTED: Original subscription check
	get allowedInvitingManagers(): boolean {
		if (this.isManagersCountUnlimited) {
			return false;
		}

		return this.accessAllowedManagersCount > this.activeManagersCount;
	}
	*/
	// Modified to always allow inviting managers
	get allowedInvitingManagers(): boolean {
		return true;
	}

	get currentSubscriptionId(): string {
		return this.currentUser.admin!.company.subscriptions[0]?.id;
	}

	/* PAYMENT CHECK COMMENTED: Original payment card check
	get paymentCardsExist(): boolean {
		return this.paymentCards.length > 0;
	}
	*/
	// Modified to always return true to bypass payment requirement
	get paymentCardsExist(): boolean {
		return true;
	}

	get hasData(): boolean {
		return !this.loading && Boolean(this.managers);
	}

	get isManagersPage(): boolean {
		return /^\/managers\/?$/.test(this.$route.path);
	}

	get filtersEmpty(): boolean {
		return isEqual(this.managerFilters, managersCollectionFiltersTemplate);
	}

	get emptyListPlaceholder(): string {
		if (this.filtersEmpty) {
			return 'Start inviting your Managers and Team Leads';
		}

		return getEmptyFiltersResultMessage('managers');
	}

	get loading(): boolean {
		return this.$apollo.queries.managers.loading;
	}

	get managersEmpty(): boolean {
		if (!this.managers) {
			return false;
		}
		return this.managers.length === 0;
	}

	get noCompany(): boolean {
		return !this.company?.id;
	}

	get managerArgs(): Record<string, any> {
		const { page, limit } = this.paginationConfig;
		const { name, countries, states, cities, titles, skills, projects } =
			this.managerFilters;

		const nextOffset = page * limit - limit;
		const filterConditions: Manager_Bool_Exp[] = [];

		if (name) {
			filterConditions.push(makeNameQuery(name, ['user']));
		}

		if (countries?.length) {
			filterConditions.push({
				user: {
					profile: {
						country: { _in: countries },
					},
				},
			});
		}

		if (states?.length) {
			filterConditions.push({
				user: {
					profile: {
						state: getStateQueryConditions({ countries, states }),
					},
				},
			});
		}

		if (cities?.length) {
			filterConditions.push({
				user: {
					profile: {
						city: { _in: cities },
					},
				},
			});
		}

		if (titles.length) {
			filterConditions.push({
				user: {
					profile: {
						job_title: { _in: titles },
					},
				},
			});
		}

		if (skills.length) {
			filterConditions.push({
				user: {
					profile: {
						skills: {
							skill_id: {
								_in: skills,
							},
						},
					},
				},
			});
		}

		if (projects.length) {
			filterConditions.push({
				projects: {
					id: { _in: projects },
				},
			});
		}

		var confirmedConditions = {};

		if (!(this.currentUser.role === 'ADMIN')) {
			confirmedConditions = {
				confirmed: {
					_eq: true,
				},
			};
		}

		return {
			// PAYMENT CHECK COMMENTED: Original unlimited check replaced with true
			isManagersCountUnlimited: true, // Originally: this.isManagersCountUnlimited
			activeManagersCountCondition: getActiveManagersCountCondition(
				this.company.id
			),
			subscriptionId: this.company.subscriptions[0]?.id,
			offset: nextOffset,
			limit: limit,
			where: {
				_and: filterConditions,
			},
			confirmedHide: confirmedConditions,
		};
	}

	/* PAYMENT CHECK COMMENTED: Original unlimited managers check
	get isManagersCountUnlimited(): boolean {
		return false;
	}
	*/
	// Modified to always return true for unlimited managers
	get isManagersCountUnlimited(): boolean {
		return true;
	}

	get getRoleAdmin(): boolean {
		if (this.currentUser.role == 'MANAGER') {
			this.managersFilterd = this.managers?.filter(
				(e) =>
					e.user.id !== this.currentUser.id &&
					e.user.confirmed &&
					e.status != 'SUSPENDED'
			);
			this.maangerCount = this?.managersFilterd?.length;
			this.paginationConfig.total = this?.managersFilterd?.length;
			return false;
		} else {
			return true;
		}
	}

	get queryPropsToWatch(): string {
		return JSON.stringify([this.managerFilters, this.paginationConfig]);
	}

	/* PAYMENT CHECK COMMENTED: Original available managers calculation
	get availableNumberOfManagers(): number {
		return this.accessAllowedManagersCount - this.activeManagersCount;
	}
	*/
	// Modified to always return a large number
	get availableNumberOfManagers(): number {
		return 999999;
	}

	/* PAYMENT CHECK COMMENTED: Original manager limit check
	get allowedNumberOfManagersReached(): boolean {
		const managersFieldsCount = this.users.length;
		return managersFieldsCount >= this.availableNumberOfManagers;
	}
	*/
	// Modified to never reach the limit
	get allowedNumberOfManagersReached(): boolean {
		return false;
	}

	get queryParams(): {
		filters: ManagersCollectionFiltersQuery;
		pagination: PaginationConfig;
	} {
		const query = this.$route.query;
		const filters = getFiltersDataFromQuery<ManagersCollectionFiltersQuery>(
			managersCollectionFiltersTemplate,
			query
		);
		const pagination = {
			...this.paginationConfig,
			...getPaginationDataFromQuery(query),
		};

		return {
			filters,
			pagination,
		};
	}

	get isChosenPageEmpty(): boolean {
		const collectionEmpty = this.managers?.length === 0;
		const notFirstPage = this.paginationConfig.page !== 1;

		return !this.loading && collectionEmpty && notFirstPage;
	}

	@Watch('queryPropsToWatch')
	updateQuery(): void {
		if (this.isChosenPageEmpty) {
			return this.showFirstPage();
		}

		const paginationQuery = getPaginationQuery(this.paginationConfig);

		const query = joinQueryValues({
			...paginationQuery,
			...this.managerFilters,
		});

		if (isEqual(query, this.$route.query)) {
			return;
		}

		this.$router.push({ query }).catch(() => { });
	}

	@Watch('$route.query', { immediate: true })
	onQueryChange(): void {
		const { filters, pagination } = this.queryParams;

		this.managerFilters = filters;
		this.paginationConfig = pagination;
	}

	@Watch('$route.query', { immediate: true })
	onRouteChange(): void {
		const queryFilters =
			getFiltersDataFromQuery<ManagersCollectionFiltersQuery>(
				managersCollectionFiltersTemplate,
				this.$route.query
			);

		const dataEqual = isEqual(queryFilters, this.managerFilters);
		if (dataEqual) {
			return;
		}

		this.managerFilters = queryFilters;
	}

	handleInviteClick() {
		/* PAYMENT CHECK COMMENTED: Original payment and subscription check
		if (this.paymentCardsExist) {
			if (this.activeManagersCount < this.accessAllowedManagersCount) {
				this.inviteDialog = !this.inviteDialog;
				return;
			} else {
				this.showExceeded = !this.showExceeded;
				return;
			}
		}
		this.showSubscriptionDialog = !this.showSubscriptionDialog;
		*/

		// Modified to always show invite dialog
		this.inviteDialog = !this.inviteDialog;
	}

	clearUsers(): void {
		this.users = [
			{
				id: null,
				email: '',
				givenName: '',
				familyName: '',
				company: '',
				valid: false,
				processing: false,
			},
		];
	}

	add(): void {
		/* PAYMENT CHECK COMMENTED: Original manager limit check
		if (this.allowedNumberOfManagersReached) {
			this.showAddAddtionalManagerDialog = true;
			return;
		}
		*/
		// Always add new user without checking limits
		this.users.push({ ...this.userTemplate });
	}

	handleDeleteRow(index: number): void {
		this.users.splice(index, 1);
		if (!this.users.length) {
			this.users = [
				{
					id: null,
					email: '',
					givenName: '',
					familyName: '',
					company: '',
					valid: false,
					processing: false,
				},
			];
		}
	}

	HandleAddAdditionalManager() {
		this.showAddAddtionalManagerDialog = false;
	}

	created(): void {
		if (this.company?.id) {
			this.$apollo.queries.managers.skip = false;
		}
	}

	mounted(): void {
		if (this.$route.params.create) {
			this.inviteDialog = true;
		}
	}

	showFirstPage(): void {
		this.paginationConfig = {
			...this.paginationConfig,
			page: paginationConfig.page,
		};
	}

	updateFilters(filters: ManagersCollectionFiltersQuery): void {
		const dataEqual = isEqual(filters, this.managerFilters);

		if (dataEqual) {
			return;
		}

		this.managerFilters = filters;
	}

	resetFilters(): void {
		if (isEqual(this.managerFilters, managersCollectionFiltersTemplate)) {
			return;
		}

		this.managerFilters = cloneDeep(managersCollectionFiltersTemplate);
		this.paginationConfig = { ...paginationConfig };
	}

	refresh(): void {
		this.$apollo.queries.managers.refetch();
	}

	message(item: Manager): void {
		this.$router.push({
			name: RouteNames.INBOX,
			query: {
				recipientUserId: item.user.id,
			},
		});
	}

	async activateManager(id: string): Promise<void> {
		/* PAYMENT CHECK COMMENTED: Original subscription check
		if (!this.allowedInvitingManagers) {
			return;
		}
		*/

		this.$confirm({
			title: 'Activate Manager',
			message: 'Are you sure?',
			button: {
				no: 'No',
				yes: 'Yes',
			},
			callback: async (confirm: any) => {
				if (confirm) {
					try {
						this.managerStatusProcessing = true;
						await this._updateManager({
							_set: { status: 'ACTIVE' },
							pk_columns: { id },
						});
						this.$eventBus.emit(
							NOTIFICATION,
							Message.successMessage('Manager was activated')
						);
						this.refresh();
					} catch (e) {
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage(e.message)
						);
					} finally {
						this.managerStatusProcessing = false;
					}
				}
			},
		});
	}

	async _updateManager(
		input: Mutation_RootUpdate_Manager_By_PkArgs
	): Promise<FetchResult> {
		return await this.$apollo.mutate({
			mutation: UPDATE_MANAGER,
			variables: input,
		});
	}
}
